import React from 'react';
import { cn } from '../../lib/utils';
import { 
  FileText, 
  Users, 
  Calendar, 
  Mail, 
  Phone, 
  MapPin, 
  Search,
  Plus,
  Settings,
  HelpCircle,
  AlertCircle,
  CheckCircle,
  Clock,
  Upload,
  Download,
  RefreshCw,
  Sparkles,
  ClipboardList,
  Workflow,
  FileEdit,
  BookOpen,
  Building2
} from 'lucide-react';

const EmptyState = ({ 
  icon,
  title,
  description,
  action,
  secondaryAction,
  variant = 'default',
  size = 'default',
  className,
  ...props 
}) => {
  const getIcon = () => {
    if (icon) return icon;
    
    const iconMap = {
      default: FileText,
      search: Search,
      users: Users,
      calendar: Calendar,
      mail: Mail,
      phone: Phone,
      location: MapPin,
      settings: Settings,
      help: HelpCircle,
      error: AlertCircle,
      success: CheckCircle,
      loading: Clock,
      upload: Upload,
      download: Download,
      refresh: RefreshCw,
      ai: Sparkles,
    };
    
    return iconMap[variant] || FileText;
  };

  const Icon = getIcon();

  const sizeClasses = {
    small: {
      container: 'py-8',
      icon: 'w-8 h-8',
      title: 'text-lg',
      description: 'text-sm',
    },
    default: {
      container: 'py-12',
      icon: 'w-12 h-12',
      title: 'text-xl',
      description: 'text-base',
    },
    large: {
      container: 'py-16',
      icon: 'w-16 h-16',
      title: 'text-2xl',
      description: 'text-lg',
    },
  };

  const variantClasses = {
    default: {
      icon: 'text-text-muted',
      title: 'text-text-primary',
      description: 'text-text-muted',
    },
    error: {
      icon: 'text-error',
      title: 'text-text-primary',
      description: 'text-text-muted',
    },
    success: {
      icon: 'text-success',
      title: 'text-text-primary',
      description: 'text-text-muted',
    },
    warning: {
      icon: 'text-warning',
      title: 'text-text-primary',
      description: 'text-text-muted',
    },
    info: {
      icon: 'text-info',
      title: 'text-text-primary',
      description: 'text-text-muted',
    },
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  return (
    <div 
      className={cn(
        'flex flex-col items-center justify-center text-center',
        currentSize.container,
        className
      )}
      {...props}
    >
      {/* Icon */}
      <div className={cn(
        'flex items-center justify-center rounded-full bg-bg-muted mb-4',
        currentSize.icon
      )}>
        <Icon className={cn(
          currentSize.icon,
          currentVariant.icon
        )} />
      </div>

      {/* Title */}
      {title && (
        <h3 className={cn(
          'font-semibold mb-2',
          currentSize.title,
          currentVariant.title
        )}>
          {title}
        </h3>
      )}

      {/* Description */}
      {description && (
        <p className={cn(
          'max-w-md mb-6',
          currentSize.description,
          currentVariant.description
        )}>
          {description}
        </p>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {action && (
            <button
              onClick={action.onClick}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-brand-primary',
                action.variant === 'primary' && 'bg-brand-primary text-text-inverted hover:bg-brand-primary/90',
                action.variant === 'secondary' && 'bg-bg-surface text-text-primary border border-border-subtle hover:bg-bg-subtle',
                action.variant === 'success' && 'bg-success text-success-fg hover:bg-success/90',
                action.variant === 'warning' && 'bg-warning text-warning-fg hover:bg-warning/90',
                action.variant === 'error' && 'bg-error text-error-fg hover:bg-error/90',
                !action.variant && 'bg-brand-primary text-text-inverted hover:bg-brand-primary/90'
              )}
            >
              {action.icon && <action.icon className="w-4 h-4" />}
              {action.label}
            </button>
          )}

          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-brand-primary',
                'bg-bg-surface text-text-primary border border-border-subtle hover:bg-bg-subtle'
              )}
            >
              {secondaryAction.icon && <secondaryAction.icon className="w-4 h-4" />}
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Predefined empty states for common scenarios
export const EmptyStates = {
  // Data-related
  NoData: ({ onAdd, ...props }) => (
    <EmptyState
      icon={FileText}
      title="No data available"
      description="There's no data to display at the moment. Add some items to get started."
      action={onAdd ? { label: 'Add Item', onClick: onAdd, icon: Plus } : undefined}
      {...props}
    />
  ),

  NoSearchResults: ({ onClear, searchTerm, ...props }) => (
    <EmptyState
      icon={Search}
      title="No results found"
      description={searchTerm ? `No results found for "${searchTerm}". Try a different search term.` : 'No results found for your search.'}
      action={onClear ? { label: 'Clear Search', onClick: onClear } : undefined}
      {...props}
    />
  ),

  // Client/User related
  NoClients: ({ onAddClient, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <Users className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No clients yet</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Start building your client base by adding your first client.</p>
        {onAddClient && (
          <button
            onClick={onAddClient}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Client
          </button>
        )}
      </div>
    );
  },

  NoTasks: ({ onCreateTask, ...props }) => (
    <EmptyState
      icon={CheckCircle}
      title="No tasks"
      description="Create your first task to start organizing your work."
      action={{ label: 'Create Task', onClick: onCreateTask, icon: Plus }}
      {...props}
    />
  ),

  // Communication
  NoMessages: ({ onSendMessage, ...props }) => (
    <EmptyState
      icon={Mail}
      title="No messages"
      description="Start a conversation by sending your first message."
      action={{ label: 'Send Message', onClick: onSendMessage, icon: Plus }}
      {...props}
    />
  ),

  NoCalls: ({ onScheduleCall, ...props }) => (
    <EmptyState
      icon={Phone}
      title="No calls scheduled"
      description="Schedule your first call to start connecting with clients."
      action={{ label: 'Schedule Call', onClick: onScheduleCall, icon: Plus }}
      {...props}
    />
  ),

  // Calendar/Events
  NoEvents: ({ onCreateEvent, ...props }) => (
    <EmptyState
      icon={Calendar}
      title="No events scheduled"
      description="Create your first event to start planning your schedule."
      action={{ label: 'Create Event', onClick: onCreateEvent, icon: Plus }}
      {...props}
    />
  ),

  // Location/Map
  NoLocations: ({ onAddLocation, ...props }) => (
    <EmptyState
      icon={MapPin}
      title="No locations"
      description="Add locations to help clients find you and your services."
      action={{ label: 'Add Location', onClick: onAddLocation, icon: Plus }}
      {...props}
    />
  ),

  // Settings/Configuration
  NoSettings: ({ onConfigure, ...props }) => (
    <EmptyState
      icon={Settings}
      title="Settings not configured"
      description="Configure your settings to customize your experience."
      action={{ label: 'Configure', onClick: onConfigure, icon: Settings }}
      {...props}
    />
  ),

  // Help/Support
  NoHelp: ({ onGetHelp, ...props }) => (
    <EmptyState
      icon={HelpCircle}
      title="Need help?"
      description="We're here to help you get started. Check out our resources or contact support."
      action={{ label: 'Get Help', onClick: onGetHelp, icon: HelpCircle }}
      secondaryAction={{ label: 'Contact Support', onClick: () => {} }}
      {...props}
    />
  ),

  // Error states
  Error: ({ onRetry, error, ...props }) => (
    <EmptyState
      icon={AlertCircle}
      title="Something went wrong"
      description={error || 'An unexpected error occurred. Please try again.'}
      action={onRetry ? { label: 'Try Again', onClick: onRetry, icon: RefreshCw } : undefined}
      variant="error"
      {...props}
    />
  ),

  // Loading states
  Loading: ({ message, ...props }) => (
    <EmptyState
      icon={Clock}
      title="Loading..."
      description={message || 'Please wait while we load your data.'}
      variant="info"
      {...props}
    />
  ),

  // Success states
  Success: ({ onContinue, message, ...props }) => (
    <EmptyState
      icon={CheckCircle}
      title="Success!"
      description={message || 'Your action was completed successfully.'}
      action={onContinue ? { label: 'Continue', onClick: onContinue } : undefined}
      variant="success"
      {...props}
    />
  ),

  // AI-related
  NoAI: ({ onEnableAI, ...props }) => (
    <EmptyState
      icon={Sparkles}
      title="AI features not enabled"
      description="Enable AI features to get intelligent insights and automation."
      action={{ label: 'Enable AI', onClick: onEnableAI, icon: Sparkles }}
      {...props}
    />
  ),

  // File/Upload related
  NoFiles: ({ onUpload, ...props }) => (
    <EmptyState
      icon={Upload}
      title="No files uploaded"
      description="Upload files to share documents with your clients."
      action={{ label: 'Upload Files', onClick: onUpload, icon: Upload }}
      {...props}
    />
  ),

  // Relocation-specific
  NoRelocationData: ({ onStartRelocation, ...props }) => (
    <EmptyState
      icon={MapPin}
      title="No relocation data"
      description="Start tracking relocation progress by adding your first relocation case."
      action={{ label: 'Start Relocation', onClick: onStartRelocation, icon: Plus }}
      {...props}
    />
  ),

  NoVisaApplications: ({ onCreateVisa, ...props }) => (
    <EmptyState
      icon={FileText}
      title="No visa applications"
      description="Create your first visa application to start the process."
      action={{ label: 'Create Application', onClick: onCreateVisa, icon: Plus }}
      {...props}
    />
  ),

  NoPropertyListings: ({ onAddProperty, ...props }) => (
    <EmptyState
      icon={MapPin}
      title="No property listings"
      description="Add property listings to help clients find their new home."
      action={{ label: 'Add Property', onClick: onAddProperty, icon: Plus }}
      {...props}
    />
  ),

  // Templates & Forms
  NoTaskTemplates: ({ onCreateTemplate, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <ClipboardList className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No task templates found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Get started by creating your first task template.</p>
        {onCreateTemplate && (
          <button
            onClick={onCreateTemplate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Template
          </button>
        )}
      </div>
    );
  },

  NoWorkflows: ({ onCreateWorkflow, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <Workflow className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No workflows found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Get started by creating your first workflow automation.</p>
        {onCreateWorkflow && (
          <button
            onClick={onCreateWorkflow}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Workflow
          </button>
        )}
      </div>
    );
  },

  NoForms: ({ onCreateForm, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <FileEdit className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No forms yet</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Create your first form or survey to start collecting responses.</p>
        {onCreateForm && (
          <button
            onClick={onCreateForm}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Form
          </button>
        )}
      </div>
    );
  },

  NoInsights: ({ onCreateItem, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <BookOpen className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No insights found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Get started by adding your first knowledge item or vendor to the insights directory.</p>
        {onCreateItem && (
          <button
            onClick={onCreateItem}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Item
          </button>
        )}
      </div>
    );
  },

  NoVendors: ({ onCreateItem, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <Building2 className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No vendors found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Get started by adding your first vendor to the directory.</p>
        {onCreateItem && (
          <button
            onClick={onCreateItem}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Vendor
          </button>
        )}
      </div>
    );
  },

  NoEmailTemplates: ({ onCreateTemplate, branding, ...props }) => {
    const primaryColor = branding?.primaryColor || '#2563eb';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" {...props}>
        <div className="flex items-center justify-center rounded-full bg-muted mb-4 w-12 h-12">
          <Mail className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">Get started by creating your first email template.</p>
        {onCreateTemplate && (
          <button
            onClick={onCreateTemplate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ 
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Template
          </button>
        )}
      </div>
    );
  },
};

export default EmptyState;
